import React, { useState } from 'react';
import { X, Briefcase, FileText, AlertCircle } from 'lucide-react';
import JobDetails from './JobDetails';
import ReferenceConfiguration from './ReferenceConfiguration';
import ProgressIndicator from '../../candidates/AddCandidateModal/ProgressIndicator';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { jobsApi, JobWithQuestions, Question } from '../../../api/jobs';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useDataRefresh } from '../../../contexts/DataRefreshContext';

interface NewJobModalProps {
  onClose: () => void;
  onJobCreated?: (job: any) => void;
}

interface QuestionGroup {
  name: string;
  weight: number;
  isActive: boolean;
}

const NewJobModal = ({ onClose, onJobCreated }: NewJobModalProps) => {
  const { t } = useLanguage();
  const { refreshJobs } = useDataRefresh();
  const [currentStep, setCurrentStep] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const steps = [t('dashboard.newJobModal.steps.details'), t('dashboard.newJobModal.steps.questions')];

  const [formData, setFormData] = useState({
    jobDetails: {
      job_title: '',
      department: '',
      application_deadline: '',
      required_number_of_references: 2,
      description: '',
    },
    questions: [] as Question[],
    groups: [] as QuestionGroup[]
  });

  const validateFirstStep = (): string[] => {
    const errors: string[] = [];
    if (!formData.jobDetails.job_title.trim()) {
      errors.push(t('dashboard.newJobModal.validation.jobTitle'));
    }
    if (!formData.jobDetails.department.trim()) {
      errors.push(t('dashboard.newJobModal.validation.department'));
    }
    if (!formData.jobDetails.application_deadline) {
      errors.push(t('dashboard.newJobModal.validation.deadline'));
    }
    return errors;
  };

  const validateQuestions = (): string[] => {
    const errors: string[] = [];
    
    // Check if there are any questions
    if (formData.questions.length === 0) {
      errors.push(t('dashboard.newJobModal.validation.questions.required'));
      return errors;
    }

    // Check if each group has at least one question
    const groupQuestions = new Map<string, number>();
    formData.questions.forEach(question => {
      const count = groupQuestions.get(question.group) || 0;
      groupQuestions.set(question.group, count + 1);
    });

    formData.groups.forEach(group => {
      const questionCount = groupQuestions.get(group.name) || 0;
      if (questionCount === 0) {
        errors.push(t('dashboard.newJobModal.validation.questions.groupEmpty').replace('{name}', group.name));
      }
    });

    // Check if any questions are empty
    formData.questions.forEach((question, index) => {
      if (!question.label.trim()) {
        errors.push(t('dashboard.newJobModal.validation.questions.emptyText').replace('{number}', String(index + 1)));
      }
    });

    // Validate total group weights equals 100
    const totalWeight = formData.groups.reduce((sum, group) => sum + group.weight, 0);
    if (totalWeight !== 100) {
      errors.push(t('dashboard.newJobModal.validation.questions.weights').replace('{total}', String(totalWeight)));
    }

    return errors;
  };

  const handleNextStep = () => {
    const errors = validateFirstStep();
    if (errors.length > 0) {
      setValidationErrors(errors);
      // Animate the error messages
      const errorContainer = document.getElementById('validation-errors');
      if (errorContainer) {
        errorContainer.classList.remove('animate-shake');
        void errorContainer.offsetWidth; // Force reflow
        errorContainer.classList.add('animate-shake');
      }
      return;
    }
    setValidationErrors([]);
    setCurrentStep(currentStep + 1);
  };

  const getStepIcon = () => {
    switch (currentStep) {
      case 0:
        return <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-white" />;
      case 1:
        return <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-white" />;
      default:
        return <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-white" />;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <JobDetails
            data={formData.jobDetails}
            onChange={(data) => {
              setFormData({ ...formData, jobDetails: data });
              setValidationErrors([]); // Clear validation errors when user makes changes
            }}
          />
        );
      case 1:
        return (
          <ReferenceConfiguration
            questions={formData.questions}
            onChange={(questions, groups) => {
              setFormData({ ...formData, questions, groups });
              setValidationErrors([]); // Clear validation errors when user makes changes
            }}
            jobData={{
              job_title: formData.jobDetails.job_title,
              department: formData.jobDetails.department,
              description: formData.jobDetails.description
            }}
          />
        );
      default:
        return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const questionErrors = validateQuestions();
    if (questionErrors.length > 0) {
      setValidationErrors(questionErrors);
      // Animate the error messages
      const errorContainer = document.getElementById('validation-errors');
      if (errorContainer) {
        errorContainer.classList.remove('animate-shake');
        void errorContainer.offsetWidth; // Force reflow
        errorContainer.classList.add('animate-shake');
      }
      return;
    }
    
    // Clear any previous errors and show confirmation
    setError(null);
    setValidationErrors([]);
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Format the date to include time and timezone
      const deadline = new Date(formData.jobDetails.application_deadline);
      deadline.setHours(23, 59, 59, 999); // Set to end of day
      
      // Assign weights to questions based on their group's weight
      const questionsWithWeights = formData.questions.map(question => {
        const group = formData.groups.find(g => g.name === question.group);
        return {
          ...question,
          weight: group?.weight || 0
        };
      });

      const jobData: JobWithQuestions = {
        job: {
          ...formData.jobDetails,
          application_deadline: deadline.toISOString(),
          employment_type: 'full-time', // Default value
          minimum_reference_level: 'mid', // Default value
        },
        questions: questionsWithWeights
      };

      console.log('Submitting job data:', jobData);
      const result = await jobsApi.createWithQuestions(jobData);
      console.log('Job creation response:', result);
      
      // Refresh the jobs data globally
      refreshJobs();
      
      // Also call the local callback if provided
      if (onJobCreated) {
        onJobCreated(result);
      }
      
      // Redirect to candidates page with the newly created job selected
      // Handle different response structures to ensure we get the job ID
      const jobId = result.id || (result.job && result.job.id);
      if (jobId) {
        window.location.href = `/candidates?jobId=${jobId}`;
      } else {
        console.error('Job ID not found in API response:', result);
        onClose();
      }
    } catch (err: any) {
      console.error('Failed to create job:', err);
      const errorMessage = err.response?.data?.detail || err.message || t('dashboard.newJobModal.error');
      setError(errorMessage);
      setShowConfirmation(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 z-10"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative">
            <div className="absolute -top-8 left-1/2 -translate-x-1/2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg flex items-center justify-center transform rotate-45">
                <div className="transform -rotate-45">
                  {getStepIcon()}
                </div>
              </div>
            </div>
          </div>

          <div className="pt-12 px-6 pb-4">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">{t('dashboard.newJobModal.title')}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {currentStep === 0 && t('dashboard.newJobModal.steps.jobDetails')}
                {currentStep === 1 && t('dashboard.newJobModal.steps.referenceQuestions')}
              </p>
            </div>

            <div className="overflow-x-auto mb-2">
              <ProgressIndicator steps={steps} currentStep={currentStep} />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="px-6 pb-4 overflow-y-auto flex-1">
              <div className="bg-gray-50 rounded-xl p-4">
                {renderStep()}
                
                {validationErrors.length > 0 && (
                  <div id="validation-errors" className="mt-4 bg-red-50 border border-red-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-2 bg-red-100 border-b border-red-200 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <span className="font-medium text-red-600 text-sm">{t('dashboard.newJobModal.validation.title')}</span>
                    </div>
                    <ul className="px-4 py-2 space-y-1">
                      {validationErrors.map((error, index) => (
                        <li key={index} className="text-sm text-red-600 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-red-600 rounded-full flex-shrink-0" />
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {error && (
                  <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center gap-3 p-4 px-6 border-t bg-gray-50 mt-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <div className="flex gap-3">
                {currentStep > 0 && (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    {t('common.back')}
                  </button>
                )}
                {currentStep < steps.length - 1 ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {t('common.continue')}
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? t('dashboard.newJobModal.creating') : t('dashboard.newJobModal.create')}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {showConfirmation && (
        <ConfirmationDialog
          title={t('dashboard.newJobModal.confirmationTitle')}
          message={t('dashboard.newJobModal.confirmationMessage')}
          onConfirm={handleConfirmSubmit}
          onCancel={() => setShowConfirmation(false)}
        />
      )}
    </>
  );
};

export default NewJobModal;